// eslint-disable-next-line no-unused-vars
import React, { useState, createContext } from "react";
import LoaderAnimation from "./LoaderAnimationLarge";
import { StateNavigator, HTML5HistoryManager } from "navigation";
import { NavigationHandler, SceneView } from "navigation-react";
import Login from "./Login";
import EditUserProfile from "./EditUserProfile";
import DashboardContent from "./DashboardContent";
import DashboardRequest from "./DashboardRequest";
import PatientRegister from "./PatientRegister";
import AddPatientRegister from "./AddPatientRegister";
import EditPatientRegister from "./EditPatientRegister";
import DeletePatientRegister from "./DeletePatientRegister";
import CtScanRequestRegister from "./CtScanRequestRegister";
import UltrasoundDashboard from "./UltrasoundDashboard";
import UserRegister from "./UserRegister";
import EditUserRegister from "./EditUserRegister";
import AddUserRegister from "./AddUserRegister";
import DeleteUserRegister from "./DeleteUserRegister";
import AddCTScanRequestRegister from "./AddCTScanRequestRegister";
import EditCTScanRequestRegister from "./EditCTScanRequestRegister";
import UpdateCTScanRequestPayout from "./UpdateCTScanRequestPayout";
import DeleteCTScanRequestRegister from "./DeleteCTScanRequestRegister";
import ViewCTScanRequestRegister from "./ViewCTScanRequestRegister";
import AddCTScanReportRegister from "./AddCTScanReportRegister";
import CtScanReportRegister from "./CtScanReportRegister";
import CtScanRequestAssignedRegister from "./CtScanRequestAssignedRegister";
import EditCTScanReportRegister from "./EditCTScanReportRegister";
import DeleteCTScanReportRegister from "./DeleteCTScanReportRegister";
import ViewCTScanReportRegister from "./ViewCTScanReportRegister";
import CtScanAssignRegister from "./CtScanAssignRegister";
import "../src/styles.css";
import { AppProvider } from "./AppContext";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

// ✅ Role & permissions context
export const RoleContext = createContext({
  activeRole: "",
  setActiveRole: () => {},
  permissions: [],
  setPermissions: () => {},
});

// ✅ Navigation setup
export const stateNavigator = new StateNavigator(
  [
    { key: "login", route: "login" },
    { key: "dashboardcontent", route: "dashboard-content" },
    {
      key: "edituserprofile",
      route: "edit-user-profile",
      trackCrumbTrail: true,
    },
    { key: "patientregister", route: "patient-register" },
    { key: "addpatientregister", route: "add-patient-register" },
    {
      key: "editpatientregister",
      route: "edit-patient-register",
      trackCrumbTrail: true,
    },
    { key: "deletepatientregister", route: "delete-patient-register" },
    { key: "dashboardrequest", route: "dashboard-request" },
    { key: "ctscanrequestregister", route: "ctscan-request-register" },
    { key: "ultrasounddashboard", route: "ultrasound-dashboard" },
    { key: "userregister", route: "user-register" },
    {
      key: "edituserregister",
      route: "edit-user-register",
      trackCrumbTrail: true,
    },
    { key: "adduserregister", route: "add-user-register" },
    { key: "deleteuserregister", route: "delete-user-register" },
    {
      key: "addctscanrequestregister",
      route: "add-ctscan-request-register",
      trackCrumbTrail: true,
    },
    { key: "editctscanrequestregister", route: "edit-ctscan-request-register" },
    {
      key: "deletectscanrequestregister",
      route: "delete-ctscan-request-register",
    },
    {
      key: "viewctscanrequestregister",
      route: "view-ctscan-request-register",
      trackCrumbTrail: true,
    },
    { key: "addctscanreportregister", route: "add-ctscan-report-register" },
    { key: "ctscanreportregister", route: "ctscan-report-register" },
    {
      key: "deletectscanreportregister",
      route: "delete-ctscan-report-register",
    },
    {
      key: "editctscanreportregister",
      route: "edit-ctscan-report-register",
      trackCrumbTrail: true,
    },
    {
      key: "viewctscanreportregister",
      route: "view-ctscan-report-register",
      trackCrumbTrail: true,
    },
    {
      key: "ctscanrequestassignedregister",
      route: "ctscan-request-assigned-register",
    },
    {
      key: "ctscancontenteditorregister",
      route: "ctscan-content-editor-register",
    },
    {
      key: "ctscanassignregister",
      route: "ctscan-assign-register",
    },
  ],
  new HTML5HistoryManager(),
);

// Redirect root `/` → `/login`
if (!window.location.pathname || window.location.pathname === "/") {
  stateNavigator.navigate("login", { replace: true });
}
stateNavigator.start();

const App = () => {
  const [activeRole, setActiveRole] = useState(ls.get("activeRole") || "");
  const [permissions, setPermissions] = useState(ls.get("permissions") || []);

  return (
    <RoleContext.Provider
      value={{ activeRole, setActiveRole, permissions, setPermissions }}
    >
      <AppProvider>
        <NavigationHandler stateNavigator={stateNavigator}>
          <SceneView active="login">
            <Login />
          </SceneView>
          <SceneView active="dashboardcontent">
            <DashboardContent />
          </SceneView>
          <SceneView active="edituserprofile">
            <EditUserProfile />
          </SceneView>
          <SceneView active="patientregister">
            <PatientRegister />
          </SceneView>
          <SceneView active="addpatientregister">
            <AddPatientRegister />
          </SceneView>
          <SceneView active="editpatientregister">
            <EditPatientRegister />
          </SceneView>
          <SceneView active="deletepatientregister">
            <DeletePatientRegister />
          </SceneView>
          <SceneView active="dashboardrequest">
            <DashboardRequest />
          </SceneView>
          <SceneView active="ctscanrequestregister">
            <CtScanRequestRegister />
          </SceneView>
          <SceneView active="ultrasounddashboard">
            <UltrasoundDashboard />
          </SceneView>
          <SceneView active="userregister">
            <UserRegister />
          </SceneView>
          <SceneView active="edituserregister">
            <EditUserRegister />
          </SceneView>
          <SceneView active="adduserregister">
            <AddUserRegister />
          </SceneView>
          <SceneView active="deleteuserregister">
            <DeleteUserRegister />
          </SceneView>
          <SceneView active="addctscanrequestregister">
            <AddCTScanRequestRegister />
          </SceneView>
          <SceneView active="editctscanrequestregister">
            <EditCTScanRequestRegister />
          </SceneView>
          <SceneView active="deletectscanrequestregister">
            <DeleteCTScanRequestRegister />
          </SceneView>
          <SceneView active="viewctscanrequestregister">
            <ViewCTScanRequestRegister />
          </SceneView>
          <SceneView active="addctscanreportregister">
            <AddCTScanReportRegister />
          </SceneView>
          <SceneView active="ctscanreportregister">
            <CtScanReportRegister />
          </SceneView>
          <SceneView active="viewctscanreportregister">
            <ViewCTScanReportRegister />
          </SceneView>
          <SceneView active="deletectscanreportregister">
            <DeleteCTScanReportRegister />
          </SceneView>
          <SceneView active="editctscanreportregister">
            <EditCTScanReportRegister />
          </SceneView>
          <SceneView active="ctscanrequestassignedregister">
            <CtScanRequestAssignedRegister />
          </SceneView>
          <SceneView active="ctscanassignregister">
            <CtScanAssignRegister />
          </SceneView>
          <SceneView active="root">
            <LoaderAnimation loadingTxt="Initializing..." />
          </SceneView>
        </NavigationHandler>
      </AppProvider>
    </RoleContext.Provider>
  );
};

export default App;
